import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../config';
const API = `${API_URL}/api`;

const AffiliatePasswordReset = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request', 'sent', 'reset', 'success'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/affiliate/password-reset/request`, {
        email: email.toLowerCase().trim()
      });
      
      if (response.data.success) {
        setStep('sent');
        setMessage(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Confirm password reset
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/affiliate/password-reset/confirm`, {
        token: token,
        new_password: newPassword
      });
      
      if (response.data.success) {
        setStep('success');
        setMessage(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-slate-900 to-blue-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            {step === 'success' ? (
              <CheckCircle className="h-8 w-8 text-green-600" />
            ) : (
              <Lock className="h-8 w-8 text-blue-600" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {step === 'request' && 'Forgot Password?'}
            {step === 'sent' && 'Check Your Email'}
            {step === 'reset' && 'Create New Password'}
            {step === 'success' && 'Password Reset!'}
          </CardTitle>
          <CardDescription>
            {step === 'request' && 'Enter your email to receive a reset link'}
            {step === 'sent' && "We've sent you a password reset link"}
            {step === 'reset' && 'Enter your new password below'}
            {step === 'success' && 'Your password has been updated'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Request Reset Form */}
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="reset-email-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
              
              <div className="text-center pt-2">
                <Link 
                  to="/affiliate/login" 
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          )}
          
          {/* Email Sent Confirmation */}
          {step === 'sent' && (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <Mail className="h-12 w-12 mx-auto text-green-600 mb-2" />
                <p className="text-green-800">{message}</p>
              </div>
              
              <div className="text-sm text-slate-600 space-y-2">
                <p>Check your inbox and click the reset link.</p>
                <p>The link will expire in <strong>1 hour</strong>.</p>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-slate-500 mb-2">Didn't receive the email?</p>
                <Button 
                  variant="outline" 
                  onClick={() => { setStep('request'); setError(''); }}
                  className="w-full"
                >
                  Try Again
                </Button>
              </div>
              
              <Link 
                to="/affiliate/login" 
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
            </div>
          )}
          
          {/* Reset Password Form */}
          {step === 'reset' && (
            <form onSubmit={handleConfirmReset} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="confirm-reset-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
          
          {/* Success Message */}
          {step === 'success' && (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-2" />
                <p className="text-green-800 font-medium">{message}</p>
              </div>
              
              <Link to="/affiliate/login">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Go to Login
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliatePasswordReset;
